import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import {
  ArrowTrendingUpIcon,
  CheckBadgeIcon,
  SparklesIcon,
  StarIcon
} from '@heroicons/react/24/outline';

const reveal = {
  hidden: { opacity: 0, y: 18 },
  visible: (index = 0) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.55,
      delay: 0.08 + index * 0.08
    }
  })
};

export default function AffiliateAmbassadorSection() {
  const t = useTranslations('affiliateProgram');

  const pillars = [
    {
      title: t('programDetails.ambassador.benefitTitles.higherPayouts'),
      text: t('programDetails.ambassador.bullets.higherPayouts'),
      icon: ArrowTrendingUpIcon,
      accent: 'text-emerald-700 bg-emerald-100'
    },
    {
      title: t('programDetails.ambassador.benefitTitles.limitedSeats'),
      text: t('programDetails.ambassador.bullets.limitedSeats'),
      icon: StarIcon,
      accent: 'text-blue-700 bg-blue-100'
    },
    {
      title: t('programDetails.ambassador.benefitTitles.earlyAccess'),
      text: t('programDetails.ambassador.bullets.earlyAccess'),
      icon: SparklesIcon,
      accent: 'text-cyan-700 bg-cyan-100'
    }
  ];

  const supportPoints = [
    t('programDetails.ambassador.bullets.closerCollaboration'),
    t('programDetails.ambassador.bullets.creatorVisibility')
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.7, ease: 'easeOut' }}
      viewport={{ once: true, amount: 0.2 }}
      className="relative"
    >
      <div className="grid gap-0 lg:grid-cols-[minmax(0,1.08fr)_minmax(320px,0.92fr)]">
        <div className="relative border-b border-slate-200/70 p-8 lg:border-b-0 lg:border-r lg:p-12">
          <div className="absolute -left-10 top-8 h-40 w-40 rounded-full bg-emerald-100 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-44 w-44 rounded-full bg-cyan-100 blur-3xl" />

          <div className="relative z-10">
            <motion.div
              custom={0}
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="max-w-3xl"
            >
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white/80 px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-700">
                <SparklesIcon className="h-4 w-4 text-emerald-600" />
                {t('programDetails.ambassador.eyebrow')}
              </div>

              <h3 className="max-w-2xl text-3xl font-bold leading-tight text-slate-900 lg:text-5xl">
                {t('programDetails.ambassador.title')}
              </h3>

              <p className="mt-5 max-w-2xl text-base leading-8 text-slate-600 lg:text-lg">
                {t('programDetails.ambassador.text')}
              </p>
            </motion.div>

            <div className="mt-10 grid gap-8 md:grid-cols-3">
              {pillars.map((pillar, index) => (
                <motion.div
                  key={pillar.title}
                  custom={index + 1}
                  variants={reveal}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="max-w-sm"
                >
                  <div className={`mb-4 flex h-11 w-11 items-center justify-center rounded-2xl ${pillar.accent}`}>
                    <pillar.icon className="h-6 w-6" />
                  </div>
                  <h4 className="text-lg font-semibold text-slate-900">
                    {pillar.title}
                  </h4>
                  <p className="mt-3 text-sm leading-7 text-slate-600">
                    {pillar.text}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        <div className="relative p-8 text-white lg:p-12">
          <div className="absolute inset-0 bg-[linear-gradient(180deg,_rgba(15,23,42,0.88),_rgba(15,23,42,0.98))]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(16,185,129,0.18),_transparent_34%),radial-gradient(circle_at_bottom_left,_rgba(34,211,238,0.12),_transparent_26%)]" />

          <div className="relative z-10 flex h-full flex-col justify-between">
            <div>
              <motion.div
                custom={0}
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="inline-flex items-center gap-2 rounded-full border border-emerald-300/20 bg-emerald-300/10 px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.24em] text-emerald-100"
              >
                <CheckBadgeIcon className="h-4 w-4 text-emerald-300" />
                {t('programDetails.ambassador.highlight.badge')}
              </motion.div>

              <motion.h4
                custom={1}
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="mt-8 max-w-md text-3xl font-semibold leading-tight text-white"
              >
                {t('programDetails.ambassador.highlight.title')}
              </motion.h4>

              <motion.p
                custom={2}
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="mt-4 max-w-md text-base leading-8 text-slate-200"
              >
                {t('programDetails.ambassador.highlight.text')}
              </motion.p>

              <motion.div
                custom={3}
                variants={reveal}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                className="mt-8 space-y-3"
              >
                {supportPoints.map((point, index) => (
                  <div
                    key={point}
                    className="flex items-start gap-3 rounded-2xl border border-white/10 bg-white/8 px-4 py-4"
                  >
                    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10 text-emerald-300">
                      <span className="text-xs font-bold">{index + 1}</span>
                    </div>
                    <p className="text-sm leading-7 text-slate-200">
                      {point}
                    </p>
                  </div>
                ))}
              </motion.div>
            </div>

            <motion.div
              custom={4}
              variants={reveal}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="mt-10 grid grid-cols-3 gap-3"
            >
              <div className="rounded-2xl border border-white/12 bg-white/10 px-4 py-4 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
                <p className="text-2xl font-bold text-white">
                  {t('programDetails.ambassador.highlight.stats.payout.value')}
                </p>
                <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-slate-300">
                  {t('programDetails.ambassador.highlight.stats.payout.label')}
                </p>
              </div>
              <div className="rounded-2xl border border-white/12 bg-white/10 px-4 py-4 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
                <p className="text-2xl font-bold text-white">
                  {t('programDetails.ambassador.highlight.stats.spots.value')}
                </p>
                <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-slate-300">
                  {t('programDetails.ambassador.highlight.stats.spots.label')}
                </p>
              </div>
              <div className="rounded-2xl border border-white/12 bg-white/10 px-4 py-4 text-center shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
                <p className="text-2xl font-bold text-white">
                  {t('programDetails.ambassador.highlight.stats.support.value')}
                </p>
                <p className="mt-1 text-[11px] uppercase tracking-[0.2em] text-slate-300">
                  {t('programDetails.ambassador.highlight.stats.support.label')}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
